/*
 * 回忆点滴第三层：一段对话的正文，以及她从这段对话里记下了什么。
 *
 * 旧版对应 `ChatHistory.tsx` + `ChatMemoryModule.tsx`：气泡列表，顶上一颗
 * 「点击查看记忆碎片」，点了弹出关键词与摘要。
 *
 * ## 气泡复用聊天页的，不另画一套
 *
 * `MessageBubble` 已经把「谁说的、头像在哪边、哪个角削平」定死了。回看历史
 * 若另写一套，同一句话在两个地方长得不一样——用户会怀疑自己记错了。
 * 这里只是**不传任何操作回调**：复制、朗读、赞踩、删除全部不出现。
 *
 *   - 朗读与删除会动灵力和数据，回忆里不该有这些入口。
 *   - 赞踩本身无害，但它属于「刚看到这句话」的当下反应，
 *     翻旧账时补一个赞对训练信号没有意义。
 *
 * `MessageBubble` 的判据是「回调传了没有」，所以不传即可，不需要加只读开关。
 *
 * ## 为什么要 import chat.css
 *
 * `.msg*` 那一套定义在 `features/chat/chat.css`，而 CSS 的加载靠各页面自己
 * import。不 import 的话气泡会退化成一堆没有样式的 div——**不报错**。
 */

import { useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { LoadingState, PageNotice } from "../../components/AsyncState";
import { Button, PageShell } from "../../components/ui";
import { IconCalendar, IconRetry } from "../../components/Icons";
import { ReadOnlyConversationLog } from "../chat/ReadOnlyConversationLog";
import {
  fetchConversationMemory,
  regenerateConversationMemory,
  type ConversationDetail,
} from "../../services/me-api";
import { ApiError } from "../../services/api-client";
import { newActionId } from "../../services/action-id";
import { notifyRelationshipChanged } from "../../services/relationship-events";
import { useAppearance } from "../../state/session";
import { useResource } from "../../state/resource";
import { timeRangeLabel } from "./format";
import { MemoryFragmentViewer } from "./MemoryFragmentViewer";
import { RegenerationConfirm } from "./RegenerationConfirm";
import "../pages.css";
import "../chat/chat.css";

export function ConversationMemoryPage() {
  const { date = "", memoryId = "" } = useParams<{ date: string; memoryId: string }>();

  const data = useResource<ConversationDetail>(
    (signal) => fetchConversationMemory(memoryId, signal),
    [memoryId],
  );
  const detail = data.state.phase === "ready" ? data.state.data : null;
  const appearance = useAppearance();

  const [memoryOpen, setMemoryOpen] = useState(false);
  const [regenerationOpen, setRegenerationOpen] = useState(false);
  const [regenerating, setRegenerating] = useState(false);
  const [regenerationError, setRegenerationError] = useState<string | null>(null);
  const regenerationActionId = useRef<string | null>(null);

  const memory = detail?.memory ?? null;
  const messages = detail?.messages ?? [];
  const failed = data.state.phase === "failed";

  /* 标题就是上一层列表里那一行：时间范围 + 标题。 */
  const heading = memory
    ? `${timeRangeLabel(memory.startedAt, memory.finishedAt)}${memory.title ?? "没留下标题的一段"}`
    : "那一段";

  /* 记忆碎片有没有内容。两样都空时不摆空弹层，只保留「重新记忆」入口。 */
  const hasMemory = memory !== null && (memory.summary !== null || memory.tags.length > 0);
  const regenerationPowerCost = detail?.memoryRegenerationPowerCost ?? null;

  const openRegeneration = () => {
    if (regenerationPowerCost === null) return;
    regenerationActionId.current = newActionId();
    setRegenerationError(null);
    setRegenerationOpen(true);
  };

  const regenerate = async () => {
    const actionId = regenerationActionId.current ?? newActionId();
    regenerationActionId.current = actionId;
    setRegenerating(true);
    setRegenerationError(null);
    try {
      await regenerateConversationMemory(memoryId, actionId);
      notifyRelationshipChanged();
      setRegenerationOpen(false);
      data.refresh();
    } catch (cause) {
      setRegenerationError(cause instanceof Error ? cause.message : "这段回忆没能重新记好");
      // 请求明确返回失败时服务端已退款，下一次应当是一笔新动作；断网或超时则
      // 复用 actionId，避免服务端其实已经完成时重复扣费。
      if (!(cause instanceof ApiError) || (cause.kind !== "offline" && cause.kind !== "timeout")) {
        regenerationActionId.current = newActionId();
      }
    } finally {
      setRegenerating(false);
    }
  };

  return (
    <PageShell
      icon={<IconCalendar size={20} />}
      title={heading}
      closeTo={`/memories/${encodeURIComponent(date)}`}
      help={
        <>
          <p>这就是你们当时说的每一句话，一句没改。</p>
          <p>
            顶上那颗心点开，是{appearance.name}从这段话里记下来的东西——
            几个关键词，加一小段她自己的复述。以后聊到这些词，她就会想起这一段。
          </p>
        </>
      }
      actions={
        failed ? (
          <Button tone="quiet" icon={<IconRetry size={15} />} onClick={data.reload}>
            重试
          </Button>
        ) : undefined
      }
    >
      {data.state.phase === "loading" ? <LoadingState label="正在把那段话找出来" /> : null}

      {data.state.phase === "failed" ? (
        <PageNotice
          scene="memory"
          error={data.state.error}
          onRetry={data.reload}
          expected={["这一段对话的全部消息", "她为这一段记下的关键词与摘要"]}
        />
      ) : null}

      {hasMemory && memory ? (
        <MemoryFragmentViewer
          memory={memory}
          open={memoryOpen}
          onOpen={() => setMemoryOpen(true)}
          onClose={() => setMemoryOpen(false)}
          action={
            <div className="memory-regeneration-action">
              <Button
                tone="quiet"
                className="memory-regeneration-button"
                onClick={openRegeneration}
              >
                重新记忆
              </Button>
            </div>
          }
        />
      ) : null}

      {detail && !hasMemory ? (
        <>
          <p className="inline-hint">这一段她还没来得及记下什么。</p>
          <div className="memory-regeneration-action">
            <Button
              tone="quiet"
              className="memory-regeneration-button"
              onClick={openRegeneration}
            >
              重新记忆
            </Button>
          </div>
        </>
      ) : null}

      {messages.length > 0 ? (
        <ReadOnlyConversationLog
          messages={messages}
          speakerName={appearance.name}
          avatarUrl={appearance.avatarUrl}
        />
      ) : null}

      {detail && messages.length === 0 ? (
        <p className="inline-hint">这一段的消息已经不在了。</p>
      ) : null}

      {regenerationPowerCost !== null ? (
        <RegenerationConfirm
          open={regenerationOpen}
          title="重新记忆这段对话吗？"
          description={`她会根据这段仍然保留的对话，重新整理关键词与摘要。这次会消耗 ${regenerationPowerCost} 点灵力。`}
          confirmLabel={`消耗 ${regenerationPowerCost} 灵力，重新记忆`}
          pendingLabel="正在重新记忆…"
          pending={regenerating}
          error={regenerationError}
          onConfirm={() => void regenerate()}
          onClose={() => setRegenerationOpen(false)}
        />
      ) : null}
    </PageShell>
  );
}
